import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletAPI } from '../services/api';
import { useWallets } from '../WalletContext';
import api from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import './Wallets.css';

function Wallets() {
  const navigate = useNavigate();
  const { refreshWallets } = useWallets();
  const [wallets, setWallets] = useState([]);
  const [deletedWallets, setDeletedWallets] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [walletToDelete, setWalletToDelete] = useState(null);
  const [transactionCount, setTransactionCount] = useState(0);
  const [activeTab, setActiveTab] = useState('active');
  const [formData, setFormData] = useState({
    name: '',
    currency: 'EUR',
    icon: '🫙',
    wallet_type: 'personal',
    household_id: null
  });

  const currencies = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'CNY', 'INR'];
  const icons = ['🫙', '💶', '💷', '💵', '💴', '💳', '🏦', '💰', '🪙', '💸', '🐷', '🎯'];

  useEffect(() => {
    loadWallets();
    loadDeletedWallets();
    loadHouseholds();
  }, []);

  const loadWallets = async () => {
    try {
      const response = await walletAPI.list();
      setWallets(response.data);
    } catch (error) {
      console.error('Error loading pots:', error);
    }
  };

  const loadDeletedWallets = async () => {
    try {
      const response = await walletAPI.listDeleted();
      setDeletedWallets(response.data);
    } catch (error) {
      console.error('Error loading deleted pots:', error);
    }
  };

  const loadHouseholds = async () => {
    try {
      const response = await api.get('/household');
      setHouseholds(response.data);
    } catch (error) {
      console.error('Error loading households:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Prepare data
      const data = {
        name: formData.name,
        currency: formData.currency,
        icon: formData.icon,
        wallet_type: formData.wallet_type
      };
      
      // Add household_id only if household pot
      if (formData.wallet_type === 'household') {
        if (!formData.household_id) {
          alert('Please select a household');
          return;
        }
        data.household_id = parseInt(formData.household_id);
      }
      
      await walletAPI.create(data);
      setShowCreateModal(false);
      setFormData({ 
        name: '', 
        currency: 'EUR', 
        icon: '🫙',
        wallet_type: 'personal',
        household_id: null
      });
      loadWallets();
      refreshWallets();
    } catch (error) {
      console.error('Error creating pot:', error);
      alert('Failed to create pot: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id, name) => {
    // Get transaction count
    const count = await walletAPI.getTransactionCount(id);
    setTransactionCount(count);
    setWalletToDelete({ id, name });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (deleteType) => {
    if (!walletToDelete) return;

    try {
      const permanent = deleteType === 'permanent';
      await walletAPI.delete(walletToDelete.id, permanent);
      
      loadWallets();
      loadDeletedWallets();
      refreshWallets();
      
      setShowDeleteModal(false);
      setWalletToDelete(null);
    } catch (error) {
      console.error('Error deleting pot:', error);
      alert('Failed to delete pot');
    }
  };

  const handleRestore = async (id) => {
    try {
      await walletAPI.restore(id);
      loadWallets();
      loadDeletedWallets();
      refreshWallets();
    } catch (error) {
      console.error('Error restoring pot:', error);
      alert('Failed to restore pot');
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€',
      'USD': '$',
      'GBP': '£',
      'CHF': 'CHF',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CNY': '¥',
      'INR': '₹'
    };
    return symbols[currency] || currency;
  };

  const handleViewTransactions = (walletId) => {
    navigate(`/transactions?wallet=${walletId}`);
  };

  return (
    <div className="wallets-container">
      <div className="wallets-header">
        <button onClick={() => navigate('/settings')} className="back-btn">
          ← Back to Settings
        </button>
        <h1>🫙 My Pots</h1>
        <button onClick={() => setShowCreateModal(true)} className="create-wallet-btn">
          + Add Pot
        </button>
      </div>

      <div className="wallet-info-box">
        <h3>💡 How Pots Work</h3>
        <p>
          Create separate pots for different purposes or currencies. Each pot tracks income and expenses independently.
          Use the pot selector on any page to view specific pot data. No automatic conversion - keep
          your currencies separate and track exchanges manually.
        </p>
      </div>

      {/* Tabs */}
      <div className="wallet-tabs">
        <button 
          className={`wallet-tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active Pots ({wallets.length})
        </button>
        <button 
          className={`wallet-tab ${activeTab === 'deleted' ? 'active' : ''}`}
          onClick={() => setActiveTab('deleted')}
        >
          Deleted Pots ({deletedWallets.length})
        </button>
      </div>

      {/* Active Pots Tab */}
      {activeTab === 'active' && (
        wallets.length === 0 ? (
          <div className="empty-state">
            <p>No pots yet. Create your first pot to get started!</p>
            <button onClick={() => navigate('/exchange')} className="exchange-btn-inline">
              💱 Transfer Between Pots
            </button>
          </div>
        ) : (
          <>
            <div className="wallets-grid">
              {wallets.map((wallet) => (
                <div key={wallet.id} className="wallet-card">
                  <div className="wallet-icon">{wallet.icon || '🫙'}</div>
                  {wallet.wallet_type === 'household' && (
                    <div className="household-badge">
                      🏠 {wallet.household_name || 'Household'}
                    </div>
                  )}
                  <h2>{wallet.name}</h2>
                  <div className="wallet-currency">
                    {getCurrencySymbol(wallet.currency)} {wallet.currency}
                  </div>
                  <div className="wallet-balance-section">
                    <div className="balance-label">Balance</div>
                    <div className="balance-amount">
                      {getCurrencySymbol(wallet.currency)}{wallet.balance.toFixed(2)}
                    </div>
                  </div>
                  <button 
                    onClick={() => handleViewTransactions(wallet.id)}
                    className="view-transactions-btn"
                  >
                    View Transactions
                  </button>
                  <button
                    onClick={() => handleDelete(wallet.id, wallet.name)}
                    className="delete-wallet-btn"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => navigate('/exchange')} className="exchange-btn">
              💱 Transfer Between Pots
            </button>
          </>
        )
      )}

      {/* Deleted Pots Tab */}
      {activeTab === 'deleted' && (
        deletedWallets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Deleted Pots</h3>
            <p>Pots you archive will appear here and can be restored.</p>
          </div>
        ) : (
          <div className="wallets-grid">
            {deletedWallets.map((wallet) => (
              <div key={wallet.id} className="wallet-card deleted">
                <div className="deleted-banner">🗄️ Archived</div>
                <div className="wallet-icon">{wallet.icon || '🫙'}</div>
                {wallet.wallet_type === 'household' && (
                  <div className="household-badge">
                    🏠 {wallet.household_name || 'Household'}
                  </div>
                )}
                <h2>{wallet.name}</h2>
                <div className="wallet-currency">
                  {getCurrencySymbol(wallet.currency)} {wallet.currency}
                </div>
                <div className="wallet-balance-section">
                  <div className="balance-label">Balance (at deletion)</div>
                  <div className="balance-amount">
                    {getCurrencySymbol(wallet.currency)}{wallet.balance.toFixed(2)}
                  </div>
                </div>
                <button 
                  onClick={() => handleRestore(wallet.id)}
                  className="restore-wallet-btn"
                >
                  ↩️ Restore
                </button>
                <button
                  onClick={() => handleDelete(wallet.id, wallet.name)}
                  className="delete-wallet-btn"
                >
                  Delete Permanently
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Pot</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Pot Type</label>
                <div className="wallet-type-selector">
                  <label className={`type-option ${formData.wallet_type === 'personal' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="wallet_type"
                      value="personal"
                      checked={formData.wallet_type === 'personal'}
                      onChange={(e) => setFormData({ ...formData, wallet_type: e.target.value, household_id: null })}
                    />
                    <span>👤 Personal Pot</span>
                  </label>
                  <label className={`type-option ${formData.wallet_type === 'household' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="wallet_type"
                      value="household"
                      checked={formData.wallet_type === 'household'}
                      onChange={(e) => setFormData({ ...formData, wallet_type: e.target.value })}
                    />
                    <span>🏠 Household Pot</span>
                  </label>
                </div>
              </div>

              {formData.wallet_type === 'household' && (
                <div className="form-group">
                  <label>Household</label>
                  <select
                    value={formData.household_id || ''}
                    onChange={(e) => setFormData({ ...formData, household_id: e.target.value })}
                    required
                  >
                    <option value="">Select household...</option>
                    {households.map((household) => (
                      <option key={household.id} value={household.id}>
                        {household.name}
                      </option>
                    ))}
                  </select>
                  {households.length === 0 && (
                    <p className="help-text">
                      No households found. Create a household first in Settings.
                    </p>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Pot Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={formData.wallet_type === 'household' ? 'e.g., Shared Expenses' : 'e.g., Savings, Bills, Fun Money'}
                  required
                />
              </div>

              <div className="form-group">
                <label>Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  {currencies.map((curr) => (
                    <option key={curr} value={curr}>
                      {curr} ({getCurrencySymbol(curr)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Icon</label>
                <div className="icon-picker">
                  {icons.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      className={`icon-option ${formData.icon === icon ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, icon })}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Create Pot
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && walletToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setWalletToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          itemType="Pot"
          itemName={walletToDelete.name}
          showArchiveOption={true}
          relatedItemsCount={transactionCount}
        />
      )}
    </div>
  );
}

export default Wallets;
